import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Eye, Clock, Users, Bomb, DollarSign, Trophy, Shield, Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

const ADMIN_USER_IDS = [
  '604881c9-cf49-493c-9301-8b86de3fd8f2', // adam@sabotagefantasy.com
  'be804d98-cc91-450a-a4d4-afccd5df57bc', // adamneilemerson@gmail.com
];

interface Draft {
  id: string;
  league_id: string;
  status: string;
  draft_type: string;
  rounds: number;
  current_round: number;
  current_pick: number;
  current_team_id: string | null;
  pick_deadline_at: string | null;
  started_at: string | null;
  hours_per_pick: number;
  pause_overnight: boolean;
  pause_start_hour: number;
  pause_end_hour: number;
}

interface DraftPick {
  id: string;
  draft_id: string;
  team_id: string;
  player_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
  is_auto_pick: boolean;
  picked_at: string;
}

interface DraftOrderEntry {
  id: string;
  team_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
}

interface Team {
  id: string;
  name: string;
  user_id: string;
  sabotage_balance: number;
}

interface Player {
  id: string;
  full_name: string;
  position_abbreviation: string;
  mlb_team_abbreviation: string;
  projected_trash_points: number | null;
  adp: number | null;
}

interface Sabotage {
  id: string;
  draft_id: string;
  applied_by_team_id: string;
  target_team_id: string;
  sabotage_name: string;
  sabotage_icon: string;
  effect_type: string;
  status: string;
  applied_at_pick: number;
}

interface League {
  id: string;
  name: string;
  sabotage_revenue: number;
}

// Hook-bearing body. Only mounted for a confirmed super-admin (see the
// GodModeDraft wrapper below), so the admin-only god-mode-draft fetch and the
// realtime subscription never run for a non-admin. Every hook runs
// unconditionally here (no early return above them), which is what satisfies
// react-hooks/rules-of-hooks; the old early-return admin gate used to sit above
// these hooks and could throw "Rendered more hooks than during the previous
// render" once a render passed the gate.
const GodModeDraftBody = ({ draftId }: { draftId?: string }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [order, setOrder] = useState<DraftOrderEntry[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Map<string, Player>>(new Map());
  const [sabotages, setSabotages] = useState<Sabotage[]>([]);
  const [league, setLeague] = useState<League | null>(null);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState('');
  const feedEndRef = useRef<HTMLDivElement>(null);

  const fetchAll = useCallback(async () => {
    if (!draftId) return;

    // Use edge function to bypass RLS
    const { data: result, error } = await supabase.functions.invoke('god-mode-draft', {
      body: { draft_id: draftId },
    });

    if (error || !result?.draft) {
      console.error('[GodMode] Failed to load:', error || result?.error);
      setLoading(false);
      return;
    }

    setDraft(result.draft as Draft);
    setOrder(result.order || []);
    setPicks(result.picks as DraftPick[] || []);
    setTeams(result.teams || []);
    setSabotages((result.sabotages || []) as Sabotage[]);
    setLeague(result.league as League);

    if (result.players?.length > 0) {
      setPlayers(new Map(result.players.map((p: Player) => [p.id, p])));
    }

    setLoading(false);
  }, [draftId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Realtime subscriptions
  useEffect(() => {
    if (!draftId) return;

    const channel = supabase
      .channel(`god-draft-${draftId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'drafts', filter: `id=eq.${draftId}` },
        (payload) => { if (payload.new) setDraft(payload.new as Draft); })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'draft_picks', filter: `draft_id=eq.${draftId}` },
        async (payload) => {
          const pick = payload.new as DraftPick;
          setPicks(prev => [...prev, pick]);
          // Fetch player info for this pick
          const { data: p } = await supabase.from('players')
            .select('id, full_name, position_abbreviation, mlb_team_abbreviation, projected_trash_points, adp')
            .eq('id', pick.player_id).single();
          if (p) setPlayers(prev => new Map(prev).set(p.id, p as Player));
        })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'draft_applied_sabotages', filter: `draft_id=eq.${draftId}` },
        () => {
          supabase.from('draft_applied_sabotages').select('*').eq('draft_id', draftId)
            .then(({ data }) => { if (data) setSabotages(data as Sabotage[]); });
        })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'draft_order', filter: `draft_id=eq.${draftId}` },
        () => {
          supabase.from('draft_order').select('*').eq('draft_id', draftId).order('pick_number')
            .then(({ data }) => { if (data) setOrder(data); });
        })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [draftId]);

  // Timer tick
  useEffect(() => {
    if (!draft?.pick_deadline_at) { setTimer('--'); return; }
    const tick = () => {
      const remaining = new Date(draft.pick_deadline_at!).getTime() - Date.now();
      if (remaining <= 0) { setTimer('EXPIRED'); return; }
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      setTimer(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [draft?.pick_deadline_at]);

  // Scroll feed to bottom on new picks
  useEffect(() => {
    setTimeout(() => feedEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [picks.length]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground">Loading draft data...</p>
    </div>
  );

  if (!draft) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground">Draft not found.</p>
    </div>
  );

  const teamMap = new Map(teams.map(t => [t.id, t]));
  const onClockOrderEntry = order.find(o => o.pick_number === draft.current_pick);
  const onClockTeam = onClockOrderEntry ? teamMap.get(onClockOrderEntry.team_id) : null;
  const totalPicks = order.length;
  const picksMade = picks.length;

  // Build rosters: team_id -> Player[]
  const rosters = new Map<string, Player[]>();
  teams.forEach(t => rosters.set(t.id, []));
  picks.forEach(p => {
    const player = players.get(p.player_id);
    if (player) {
      const roster = rosters.get(p.team_id);
      if (roster) roster.push(player);
    }
  });

  // Draft board: round -> { team_id -> pick }
  const boardByRound = new Map<number, Map<string, { player: Player | null; pickNumber: number; isAuto: boolean }>>();
  for (let r = 1; r <= draft.rounds; r++) boardByRound.set(r, new Map());
  picks.forEach(p => {
    const player = players.get(p.player_id) || null;
    boardByRound.get(p.round)?.set(p.team_id, { player, pickNumber: p.pick_number, isAuto: p.is_auto_pick });
  });

  // R1 team order for board columns
  const r1Order = order.filter(o => o.round === 1).sort((a, b) => a.pick_number - b.pick_number).map(o => o.team_id);

  const activeSabotages = sabotages.filter(s => s.status === 'active' || s.status === 'pending');
  const sabotageRevenue = league?.sabotage_revenue || 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold font-heading">God Mode</span>
            <Badge variant="outline" className="text-[10px]">{league?.name}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{picksMade}/{totalPicks} picks</span>
            <Badge className={cn(
              "text-[10px]",
              draft.status === 'in_progress' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
              draft.status === 'completed' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
              'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
            )}>{draft.status}</Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 max-w-7xl space-y-4">

        {/* On the Clock + Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* On the Clock */}
          <Card className="md:col-span-2 border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-primary" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">On the Clock: Pick {draft.current_pick} (R{draft.current_round})</p>
                  <p className="text-lg font-bold">{onClockTeam?.name || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Time Left</p>
                  <p className={cn("text-lg font-mono font-bold", timer === 'EXPIRED' ? 'text-destructive' : 'text-foreground')}>{timer}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prize Pool / Revenue */}
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-xs text-muted-foreground">Sabotage Revenue</p>
                <p className="text-lg font-bold text-green-400">${sabotageRevenue}</p>
              </div>
            </CardContent>
          </Card>

          {/* Sabotages Count */}
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Bomb className="w-5 h-5 text-orange-400" />
              <div>
                <p className="text-xs text-muted-foreground">Active Sabotages</p>
                <p className="text-lg font-bold">{activeSabotages.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Sabotages Detail */}
        {activeSabotages.length > 0 && (
          <Card className="border-orange-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Bomb className="w-4 h-4 text-orange-400" />Active Sabotages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {activeSabotages.map(s => (
                <div key={s.id} className="flex items-center gap-2 text-sm p-2 rounded bg-muted/30">
                  <span>{s.sabotage_icon}</span>
                  <span className="font-medium">{s.sabotage_name}</span>
                  <span className="text-muted-foreground">-</span>
                  <span className="text-muted-foreground">{teamMap.get(s.applied_by_team_id)?.name}</span>
                  <span className="text-muted-foreground">&rarr;</span>
                  <span>{teamMap.get(s.target_team_id)?.name}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">{s.status}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Live Pick Feed */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-primary" />Live Pick Feed</CardTitle>
            </CardHeader>
            <ScrollArea className="h-[500px]">
              <CardContent className="space-y-1 pr-4">
                {picks.map(p => {
                  const player = players.get(p.player_id);
                  const team = teamMap.get(p.team_id);
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-xs p-2 rounded bg-muted/20">
                      <span className="font-mono text-muted-foreground w-8 text-right shrink-0">#{p.pick_number}</span>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold">{player?.full_name || 'Unknown'}</span>
                        <span className="text-muted-foreground ml-1">{player?.position_abbreviation} {player?.mlb_team_abbreviation}</span>
                      </div>
                      <span className="text-muted-foreground truncate max-w-[80px]">{team?.name}</span>
                      {p.is_auto_pick && <Badge variant="secondary" className="text-[8px] px-1">AUTO</Badge>}
                    </div>
                  );
                })}
                {picks.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No picks yet</p>}
                <div ref={feedEndRef} />
              </CardContent>
            </ScrollArea>
          </Card>

          {/* Team Rosters */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-primary" />Team Rosters ({teams.length} teams)</CardTitle>
            </CardHeader>
            <ScrollArea className="h-[500px]">
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {teams.map(team => {
                    const roster = rosters.get(team.id) || [];
                    const trashTotal = roster.reduce((s, p) => s + (p.projected_trash_points || 0), 0);
                    return (
                      <div key={team.id} className="border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold truncate">{team.name}</p>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-[10px]">{roster.length} players</Badge>
                            <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">{trashTotal.toFixed(0)} pts</Badge>
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          {roster.map(p => (
                            <div key={p.id} className="flex items-center gap-1 text-[11px]">
                              <span className="text-muted-foreground w-6">{p.position_abbreviation}</span>
                              <span className="flex-1 truncate">{p.full_name}</span>
                              <span className="text-muted-foreground">{p.mlb_team_abbreviation}</span>
                            </div>
                          ))}
                          {roster.length === 0 && <p className="text-[11px] text-muted-foreground italic">No picks yet</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </ScrollArea>
          </Card>
        </div>

        {/* Draft Board */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" />Draft Board</CardTitle>
          </CardHeader>
          <ScrollArea className="w-full">
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-1.5 text-muted-foreground font-medium sticky left-0 bg-card z-10 w-12">Rd</th>
                      {r1Order.map(tid => (
                        <th key={tid} className="p-1.5 text-center font-medium text-muted-foreground min-w-[100px] max-w-[120px] truncate">
                          {teamMap.get(tid)?.name || '?'}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: draft.rounds }, (_, i) => i + 1).map(round => {
                      const roundData = boardByRound.get(round);
                      return (
                        <tr key={round} className="border-t border-border/50">
                          <td className="p-1.5 text-muted-foreground font-mono sticky left-0 bg-card z-10">{round}</td>
                          {r1Order.map(tid => {
                            const cell = roundData?.get(tid);
                            return (
                              <td key={`${round}-${tid}`} className={cn(
                                "p-1.5 text-center",
                                cell ? 'bg-primary/5' : ''
                              )}>
                                {cell ? (
                                  <div className="truncate">
                                    <span className="font-medium">{cell.player?.full_name || '?'}</span>
                                    <br />
                                    <span className="text-muted-foreground">{cell.player?.position_abbreviation} {cell.player?.mlb_team_abbreviation}</span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground/30">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </ScrollArea>
        </Card>

      </main>
    </div>
  );
};

// Thin admin gate. Runs its hooks (useParams + useAuth) unconditionally and
// branches on RENDER only, so it never calls hooks conditionally. Every
// non-admin is redirected and the hook-bearing body never mounts for them, so
// no god-mode data is requested client-side for a non-admin: the security gate
// is unchanged by the hooks refactor (if anything it is stricter, since the
// fetch no longer fires during the auth-loading window before admin is known).
const GodModeDraft = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const { user } = useAuth();

  // Auth still resolving: neutral loader, never a flash of admin UI.
  if (!user) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  }

  // Non-admin: always redirect. Body (with its admin-only fetch + subscription)
  // is not mounted.
  if (!ADMIN_USER_IDS.includes(user.id)) {
    return <Navigate to="/" replace />;
  }

  return <GodModeDraftBody draftId={draftId} />;
};

export default GodModeDraft;
